import React, { useState, useMemo, useEffect } from "react";
import { toast } from "sonner";
import { Download, Search, ChevronLeft, ChevronRight, X, Mail, Phone, Globe, FileText, Archive, ArchiveRestore, Trash2, CheckSquare, Square } from "lucide-react";
import { API, api } from "@/lib/api";

const CONTACT_FIELDS = [
  ["reason", "Reason for contact"], ["first_name", "First name"], ["last_name", "Last name"],
  ["company", "Company"], ["position", "Position"], ["email", "Email"], ["phone", "Phone"],
  ["country", "Country"], ["org_type", "Organization type"], ["area_of_interest", "Area of interest"],
  ["message", "Message"], ["consent", "Consent given"],
];
const OPP_FIELDS = [
  ["company", "Company"], ["website", "Website"], ["founder_name", "Founder name"],
  ["founder_email", "Founder email"], ["headquarters", "Headquarters"], ["sector", "Sector"],
  ["company_stage", "Company stage"], ["funding_stage", "Funding stage"], ["amount_raising", "Amount raising"],
  ["current_revenue", "Current revenue"], ["saudi_presence", "Saudi presence"], ["saudi_interest", "Saudi expansion interest"],
  ["investment_summary", "Investment summary"], ["partnership_opportunity", "Partnership opportunity"],
  ["why_intersect", "Why Intersect"],
];

const PER_PAGE = 8;

function highlight(text, q) {
  if (!q || !text) return text;
  const i = String(text).toLowerCase().indexOf(q.toLowerCase());
  if (i === -1) return text;
  const s = String(text);
  return (<>{s.slice(0, i)}<mark className="bg-primary-gold/30 text-ivory">{s.slice(i, i + q.length)}</mark>{s.slice(i + q.length)}</>);
}

function DetailModal({ item, type, onClose, onPrev, onNext, index, total }) {
  const fields = type === "contacts" ? CONTACT_FIELDS : OPP_FIELDS;
  const title = (type === "contacts" ? `${item.first_name || ""} ${item.last_name || ""}`.trim() : item.company) || "Submission";
  return (
    <div className="fixed inset-0 z-[80] bg-obsidian/80 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-6" data-testid="submission-detail-modal">
      <div className="bg-charcoal border border-primary-gold/20 w-full max-w-2xl my-10">
        <div className="flex items-center justify-between p-6 border-b border-white/5">
          <div>
            <p className="eyebrow mb-1">{type === "contacts" ? "Contact Message" : "Opportunity"}</p>
            <h3 className="text-xl font-light text-ivory">{title}</h3>
            <p className="text-xs text-muted-text mt-1">{new Date(item.created_at).toLocaleString()}</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onPrev} disabled={index <= 0} className="text-muted-text hover:text-primary-gold disabled:opacity-30" data-testid="submission-prev"><ChevronLeft size={20} /></button>
            <span className="text-xs text-muted-text">{index + 1} / {total}</span>
            <button onClick={onNext} disabled={index >= total - 1} className="text-muted-text hover:text-primary-gold disabled:opacity-30" data-testid="submission-next"><ChevronRight size={20} /></button>
            <button onClick={onClose} className="ml-3 text-muted-text hover:text-ivory" data-testid="submission-detail-close"><X size={20} /></button>
          </div>
        </div>

        <div className="p-6 space-y-4">
          <div className="flex flex-wrap gap-3">
            {item.email || item.founder_email ? (
              <a href={`mailto:${item.email || item.founder_email}`} className="btn-gold text-xs" data-testid="submission-email-link"><Mail size={14} strokeWidth={1.5} /> Reply by email</a>
            ) : null}
            {item.phone && <a href={`tel:${item.phone}`} className="btn-gold text-xs"><Phone size={14} strokeWidth={1.5} /> Call</a>}
            {item.website && <a href={item.website} target="_blank" rel="noreferrer" className="btn-gold text-xs"><Globe size={14} strokeWidth={1.5} /> Website</a>}
            {item.pitch_deck && <a href={`${API}/admin/uploads/${item.pitch_deck}`} target="_blank" rel="noreferrer" className="btn-gold text-xs" data-testid="submission-deck-link"><FileText size={14} strokeWidth={1.5} /> Pitch deck</a>}
          </div>
          <div className="divide-y divide-white/5 border-t border-white/5">
            {fields.map(([k, label]) => {
              let v = item[k];
              if (v === undefined || v === null || v === "") return null;
              if (typeof v === "boolean") v = v ? "Yes" : "No";
              return (
                <div key={k} className="grid grid-cols-1 sm:grid-cols-3 gap-2 py-3" data-testid={`detail-${k}`}>
                  <span className="text-xs tracking-wide uppercase text-muted-text">{label}</span>
                  <span className="sm:col-span-2 text-sm text-soft-stone whitespace-pre-wrap break-words">{String(v)}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Submissions({ contacts, opportunities, exportCsv, reload }) {
  const [subTab, setSubTab] = useState("contacts");
  const [view, setView] = useState("active"); // active | archived
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(0);
  const [openIdx, setOpenIdx] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [busy, setBusy] = useState(false);

  const all = subTab === "contacts" ? contacts : opportunities;

  const byView = useMemo(
    () => all.filter((r) => (view === "archived" ? r.archived === true : r.archived !== true)),
    [all, view]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return byView;
    return byView.filter((r) => JSON.stringify(r).toLowerCase().includes(q));
  }, [byView, query]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const current = Math.min(page, pageCount - 1);
  const rows = filtered.slice(current * PER_PAGE, current * PER_PAGE + PER_PAGE);

  // Clear selection whenever the working set changes.
  useEffect(() => { setSelected(new Set()); }, [subTab, view]);

  const archivedCount = useMemo(() => all.filter((r) => r.archived === true).length, [all]);
  const activeCount = all.length - archivedCount;

  const switchTab = (s) => { setSubTab(s); setQuery(""); setPage(0); setOpenIdx(null); };
  const switchView = (v) => { setView(v); setQuery(""); setPage(0); setOpenIdx(null); };

  const toggle = (id) => setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allFilteredSelected = filtered.length > 0 && filtered.every((r) => selected.has(r.id));
  const toggleAll = () => setSelected(() => (allFilteredSelected ? new Set() : new Set(filtered.map((r) => r.id))));

  const runAction = async (action) => {
    const ids = [...selected];
    if (ids.length === 0) return;
    if (action === "delete" && !window.confirm(`Permanently delete ${ids.length} submission${ids.length > 1 ? "s" : ""}? This cannot be undone.`)) return;
    setBusy(true);
    try {
      if (action === "delete") {
        await api.post(`/admin/submissions/${subTab}/delete`, { ids });
        toast.success(`Deleted ${ids.length} submission${ids.length > 1 ? "s" : ""}.`);
      } else {
        const archived = action === "archive";
        await api.post(`/admin/submissions/${subTab}/archive`, { ids, archived });
        toast.success(`${archived ? "Archived" : "Restored"} ${ids.length} submission${ids.length > 1 ? "s" : ""}.`);
      }
      setSelected(new Set());
      setOpenIdx(null);
      if (reload) await reload();
    } catch { toast.error("Action failed."); } finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-submissions">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <h1 className="text-3xl font-light text-ivory">Submissions</h1>
        <div className="flex gap-3">
          <button onClick={() => exportCsv("contacts")} className="btn-gold" data-testid="export-contacts"><Download size={15} strokeWidth={1.5} /> Contacts CSV</button>
          <button onClick={() => exportCsv("opportunities")} className="btn-gold" data-testid="export-opportunities"><Download size={15} strokeWidth={1.5} /> Opportunities CSV</button>
        </div>
      </div>

      <div className="flex flex-col md:flex-row md:items-center gap-4 mb-4">
        <div className="flex gap-2">
          {["contacts", "opportunities"].map((s) => (
            <button key={s} onClick={() => switchTab(s)} data-testid={`subtab-${s}`}
              className={`px-4 py-2 text-xs uppercase tracking-wide border ${subTab === s ? "border-primary-gold text-primary-gold" : "border-white/10 text-muted-text"}`}>
              {s} <span className="opacity-60">({s === "contacts" ? contacts.length : opportunities.length})</span>
            </button>
          ))}
        </div>
        <div className="relative flex-1 md:max-w-md">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-text" />
          <input value={query} onChange={(e) => { setQuery(e.target.value); setPage(0); }} placeholder={`Search ${subTab}…`} className="field pl-9" data-testid="submission-search" />
        </div>
      </div>

      <div className="flex items-center gap-2 mb-6">
        <button onClick={() => switchView("active")} data-testid="view-active" className={`px-3 py-1.5 text-[11px] uppercase tracking-wide border ${view === "active" ? "border-primary-gold text-primary-gold" : "border-white/10 text-muted-text"}`}>Active ({activeCount})</button>
        <button onClick={() => switchView("archived")} data-testid="view-archived" className={`px-3 py-1.5 text-[11px] uppercase tracking-wide border ${view === "archived" ? "border-primary-gold text-primary-gold" : "border-white/10 text-muted-text"}`}>Archived ({archivedCount})</button>
      </div>

      {/* Selection toolbar */}
      {filtered.length > 0 && (
        <div className="flex items-center justify-between gap-4 flex-wrap border border-white/5 bg-charcoal px-4 py-3 mb-4" data-testid="submission-toolbar">
          <button onClick={toggleAll} className="flex items-center gap-2 text-sm text-soft-stone hover:text-primary-gold" data-testid="select-all">
            {allFilteredSelected ? <CheckSquare size={16} className="text-primary-gold" /> : <Square size={16} />}
            {selected.size > 0 ? `${selected.size} selected` : "Select all"}
          </button>
          {selected.size > 0 && (
            <div className="flex items-center gap-3" data-testid="bulk-actions">
              {view === "active" ? (
                <button onClick={() => runAction("archive")} disabled={busy} className="btn-gold text-xs disabled:opacity-40" data-testid="bulk-archive"><Archive size={14} strokeWidth={1.5} /> Archive</button>
              ) : (
                <button onClick={() => runAction("unarchive")} disabled={busy} className="btn-gold text-xs disabled:opacity-40" data-testid="bulk-unarchive"><ArchiveRestore size={14} strokeWidth={1.5} /> Restore</button>
              )}
              <button onClick={() => runAction("delete")} disabled={busy} className="text-xs flex items-center gap-1.5 px-4 py-2 border border-destructive/50 text-destructive hover:bg-destructive/10 transition-colors disabled:opacity-40" data-testid="bulk-delete"><Trash2 size={14} strokeWidth={1.5} /> Delete</button>
              <button onClick={() => setSelected(new Set())} className="text-muted-text hover:text-ivory" data-testid="clear-selection"><X size={16} /></button>
            </div>
          )}
        </div>
      )}

      <div className="space-y-3">
        {filtered.length === 0 && <p className="text-muted-text" data-testid="submissions-empty">{query ? "No submissions match your search." : view === "archived" ? "No archived submissions." : "No submissions yet."}</p>}
        {rows.map((r) => {
          const globalIdx = filtered.indexOf(r);
          const isSel = selected.has(r.id);
          return (
            <div key={r.id} className={`flex items-stretch gap-3 border bg-charcoal transition-colors ${isSel ? "border-primary-gold/50" : "border-white/5 hover:border-primary-gold/30"}`} data-testid="submission-row">
              <button onClick={() => toggle(r.id)} className="flex items-center pl-4 text-muted-text hover:text-primary-gold" data-testid="row-checkbox" aria-label="select">
                {isSel ? <CheckSquare size={16} className="text-primary-gold" /> : <Square size={16} />}
              </button>
              <button onClick={() => setOpenIdx(globalIdx)} className="flex-1 text-left p-5 pl-2" data-testid="submission-open">
                {subTab === "contacts" ? (
                  <>
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <span className="text-ivory">{highlight(`${r.first_name} ${r.last_name}`, query)} {r.company && <span className="text-muted-text">· {highlight(r.company, query)}</span>}</span>
                      <span className="text-xs text-muted-text">{new Date(r.created_at).toLocaleString()}</span>
                    </div>
                    <p className="text-xs text-primary-gold mt-1">{r.reason} · {highlight(r.email, query)} {r.country && `· ${r.country}`}</p>
                    <p className="text-sm text-soft-stone mt-3 line-clamp-2">{highlight(r.message, query)}</p>
                  </>
                ) : (
                  <>
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <span className="text-ivory">{highlight(r.company, query)} <span className="text-muted-text">· {highlight(r.founder_name, query)}</span></span>
                      <span className="text-xs text-muted-text">{new Date(r.created_at).toLocaleString()}</span>
                    </div>
                    <p className="text-xs text-primary-gold mt-1">{r.sector} · {highlight(r.founder_email, query)} {r.amount_raising && `· Raising ${r.amount_raising}`}</p>
                    {r.investment_summary && <p className="text-sm text-soft-stone mt-3 line-clamp-2">{highlight(r.investment_summary, query)}</p>}
                  </>
                )}
              </button>
            </div>
          );
        })}
      </div>

      {pageCount > 1 && (
        <div className="flex items-center justify-between mt-8" data-testid="submissions-pagination">
          <span className="text-xs text-muted-text">Showing {current * PER_PAGE + 1}–{Math.min((current + 1) * PER_PAGE, filtered.length)} of {filtered.length}</span>
          <div className="flex items-center gap-3">
            <button onClick={() => setPage(current - 1)} disabled={current <= 0} className="btn-gold text-xs disabled:opacity-30" data-testid="page-prev"><ChevronLeft size={14} /> Prev</button>
            <span className="text-xs text-muted-text">{current + 1} / {pageCount}</span>
            <button onClick={() => setPage(current + 1)} disabled={current >= pageCount - 1} className="btn-gold text-xs disabled:opacity-30" data-testid="page-next">Next <ChevronRight size={14} /></button>
          </div>
        </div>
      )}

      {openIdx !== null && filtered[openIdx] && (
        <DetailModal
          item={filtered[openIdx]}
          type={subTab}
          index={openIdx}
          total={filtered.length}
          onClose={() => setOpenIdx(null)}
          onPrev={() => setOpenIdx((i) => Math.max(0, i - 1))}
          onNext={() => setOpenIdx((i) => Math.min(filtered.length - 1, i + 1))}
        />
      )}
    </div>
  );
}
